import { Color4, Engine, Scene, UniversalCamera, Vector3 } from "@babylonjs/core";
import { IScene, SceneFactory } from "@dreamcam/all-in-one-player-common/Scenes";
import { AdvancedFreeCameraMouseInput, AdvancedFreeCameraTouchInput } from "@dreamcam/babylon-tools";
import { Projection, ProjectionMeshBabylonJS } from "@dreamcam/projection-mesh-babylonjs";

export class BabylonScene implements IScene<Engine> {

    //#region Private Fields

    private readonly _engine: Engine;
    private readonly _scene: Scene;
    private readonly _camera: UniversalCamera;
    private readonly _projectionMesh: ProjectionMeshBabylonJS;
    private readonly _touchInput: AdvancedFreeCameraTouchInput;
    private readonly _mouseInput: AdvancedFreeCameraMouseInput;

	private _zoomSensitivity: number = 25;
	private _touchMoveXSensitivity: number = 500;
	private _touchMoveYSensitivity: number = 500;
	private _zoomMin: number = -14;
	private _zoomMax: number = 6;
	private _touchZoomMode: "pinch" | "scroll" = "pinch";
	private _requireReleaseAfterZoom: boolean = true;
	private _moveXSensitivity: number = 2000;
	private _moveYSensitivity: number = 2000;
	private _rotationXLimit: number = 90;
	private _rotationYLimit: number = 90;
	private _cameraInitialZoomZ: number = -9;
    private _sceneColor: string = "#000000";

	//#endregion

    //#region Public Properties

    public get projectionMesh(): ProjectionMeshBabylonJS {
        return this._projectionMesh;
    }

	public get engine(): Engine {
		return this._engine;
	}

	public get zoomSensitivity(): number {
		return this._zoomSensitivity;
	}

	public set zoomSensitivity(value: number) {
		if(this.zoomSensitivity === value)
			return;
		this._zoomSensitivity = value;
        this._touchInput.zoomSensitivity = value;
	}

	public get touchMoveXSensitivity(): number {
		return this._touchMoveXSensitivity;
	}

	public set touchMoveXSensitivity(value: number) {
		if(this.touchMoveXSensitivity === value)
			return;
		this._touchMoveXSensitivity = value;
        this._touchInput.moveXSensitivity = value;
	}

	public get touchMoveYSensitivity(): number {
		return this._touchMoveYSensitivity;
	}

	public set touchMoveYSensitivity(value: number) {
		if(this.touchMoveYSensitivity === value)
			return;
		this._touchMoveYSensitivity = value;
        this._touchInput.moveYSensitivity = value;
	}

	public get zoomMin(): number {
		return this._zoomMin;
	}

	public set zoomMin(value: number) {
		if(this.zoomMin === value)
			return;
		this._zoomMin = value;
        this._touchInput.zoomMin = value;
	}

	public get zoomMax(): number {
		return this._zoomMax;
	}

	public set zoomMax(value: number) {
		if(this.zoomMax === value)
			return;
		this._zoomMax = value;
        this._touchInput.zoomMax = value;
	}

	public get touchZoomMode(): "pinch" | "scroll" {
		return this._touchZoomMode;
	}

	public set touchZoomMode(value: "pinch" | "scroll") {
		if(this.touchZoomMode === value)
			return;
		this._touchZoomMode = value;
        this._touchInput.zoomMode = value;
	}

	public get requireReleaseAfterZoom(): boolean {
		return this._requireReleaseAfterZoom;
	}

	public set requireReleaseAfterZoom(value: boolean) {
		if(this.requireReleaseAfterZoom === value)
			return;
		this._requireReleaseAfterZoom = value;
        this._touchInput.requireReleaseAfterZoom = value;
	}

	public get mouseMoveXSensitivity(): number {
		return this._moveXSensitivity;
	}

	public set mouseMoveXSensitivity(value: number) {
		if(this.mouseMoveXSensitivity === value)
			return;
		this._moveXSensitivity = value;
        this._mouseInput.moveXSensitivity = value;
	}

	public get mouseMoveYSensitivity(): number {
		return this._moveYSensitivity;
	}

	public set mouseMoveYSensitivity(value: number) {
		if(this.mouseMoveYSensitivity === value)
			return;
		this._moveYSensitivity = value;
        this._mouseInput.moveYSensitivity = value;
	}

	public get rotationXLimit(): number {
		return this._rotationXLimit;
	}

	public set rotationXLimit(value: number) {
		if(this.rotationXLimit === value)
			return;
		this._rotationXLimit = value;
        this._touchInput.rotationXLimit = value;
        this._mouseInput.rotationXLimit = value;
	}

	public get rotationYLimit(): number {
		return this._rotationYLimit;
	}

	public set rotationYLimit(value: number) {
		if(this.rotationYLimit === value)
			return;
		this._rotationYLimit = value;
        this._touchInput.rotationYLimit = value;
        this._mouseInput.rotationYLimit = value;
	}

	public get currentZoom(): number {
		return this._cameraInitialZoomZ;
	}

	public set currentZoom(value: number) {
		if(this.currentZoom === value)
			return;
		this._cameraInitialZoomZ = value;
        
	}

    public get sceneColor(): string {
        return this._sceneColor;
    }

    public set sceneColor(value: string) {
        if(this.sceneColor === value)
            return;
        this._sceneColor = value;
        this._scene.clearColor = Color4.FromHexString(value);
    }

    //#endregion

    //#region Constructor

    public constructor(canvas: HTMLCanvasElement)
    {
        this._engine = new Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true, useHighPrecisionFloats: true, powerPreference: "high-performance" });
        this._scene = new Scene(this._engine);
        this._camera = new UniversalCamera("camera", Vector3.Zero(), this._scene);
        this._projectionMesh = new ProjectionMeshBabylonJS(Projection.FromString(""), 10, this._scene);
        this._touchInput = new AdvancedFreeCameraTouchInput({
            zoomSensitivity: this.zoomSensitivity,
            moveXSensitivity: this.touchMoveXSensitivity,
            moveYSensitivity: this.touchMoveYSensitivity,
            zoomMin: this.zoomMin,
            zoomMax: this.zoomMax,
            rotationXLimit: this.rotationXLimit,
            rotationYLimit: this.rotationYLimit,
            zoomMode: this.touchZoomMode,
            requireReleaseAfterZoom: this.requireReleaseAfterZoom,
        });
        this._mouseInput =
            new AdvancedFreeCameraMouseInput({
                moveXSensitivity: this.mouseMoveXSensitivity,
                moveYSensitivity: this.mouseMoveYSensitivity,
                rotationXLimit: this.rotationXLimit,
                rotationYLimit: this.rotationYLimit,
            })
        this.init();
    }
    
    //#endregion

    //#region Methods

    public zoom(value: number): void
    {
        this._camera.fov += value;
    }
    
    private init()
    {
        this._engine.resize();

        this._scene.createDefaultEnvironment({
            enableGroundShadow: false,
            groundYBias: 0,
            createGround: true,
            groundSize: 0.0001,
            groundOpacity: 0,
            setupImageProcessing: false,
            createSkybox: false,
        });
        this._scene.clearColor = Color4.FromHexString("#00000000")

        this._camera.fovMode = UniversalCamera.FOVMODE_HORIZONTAL_FIXED
		this._camera.fov = 1.05
		this._camera.position.z = this.currentZoom
        this._camera.setTarget(Vector3.Forward())
		this._camera.attachControl()
        this._camera.inputs.removeByType('FreeCameraTouchInput');
		this._camera.inputs.removeMouse();
		this._camera.inputs.add(this._touchInput);
		this._camera.inputs.add(this._mouseInput);
		this._camera.layerMask |= this._projectionMesh?.Layers.x ?? 0

		this._projectionMesh.IsVisible = true

		this._engine?.runRenderLoop(() => {
			if (this._scene?.activeCamera) {
				this._scene?.render()
			}
		})
    }

    //#endregion
}

SceneFactory.Register(BabylonScene);